import type { ModelAppearance } from '@studio/shared';

import type { Locale } from './i18n';

/**
 * The choices offered for each part of a model's description.
 *
 * The value is English, because it is written word for word into the identity
 * line the renderer receives; the label is what the person choosing reads, in
 * their own language and capitalised. A value the brief or a photograph read
 * that is not on the list is kept and shown as it is — see `choicesFor`.
 */
type Choice = { value: string; en: string; fr: string };

const c = (value: string, fr: string, en?: string): Choice => ({
  value,
  fr,
  en: en ?? value.charAt(0).toUpperCase() + value.slice(1),
});

export const APPEARANCE_CHOICES: Partial<Record<keyof ModelAppearance, Choice[]>> = {
  ageRange: [
    c('late teens', 'Fin d’adolescence'),
    c('early 20s', 'Début de vingtaine'),
    c('mid 20s', 'Milieu de vingtaine'),
    c('late 20s', 'Fin de vingtaine'),
    c('30s', 'Trentaine'),
    c('40s', 'Quarantaine'),
    c('50s', 'Cinquantaine'),
    c('60s', 'Soixantaine et plus', '60s and over'),
  ],
  height: [
    c('petite', 'Petite'),
    c('average height', 'Moyenne'),
    c('tall', 'Grande'),
    c('very tall', 'Très grande'),
  ],
  skinTone: [
    c('very fair', 'Très claire'),
    c('fair', 'Claire'),
    c('light', 'Beige clair'),
    c('medium', 'Moyenne'),
    c('olive', 'Olive'),
    c('tan', 'Hâlée'),
    c('brown', 'Mate'),
    c('dark brown', 'Foncée'),
    c('deep', 'Très foncée'),
  ],
  eyeColor: [
    c('blue', 'Bleus'),
    c('green', 'Verts'),
    c('hazel', 'Noisette'),
    c('grey', 'Gris'),
    c('light brown', 'Marron clair'),
    c('brown', 'Marron'),
    c('dark brown', 'Marron foncé'),
    c('black', 'Noirs'),
  ],
  hairColor: [
    c('platinum blonde', 'Blond platine'),
    c('blonde', 'Blond'),
    c('strawberry blonde', 'Blond vénitien'),
    c('light brown', 'Châtain clair'),
    c('brown', 'Châtain'),
    c('dark brown', 'Brun'),
    c('black', 'Noir'),
    c('auburn', 'Auburn'),
    c('red', 'Roux'),
    c('grey', 'Gris'),
    c('white', 'Blanc'),
  ],
  hairLength: [
    c('shaved', 'Rasés'),
    c('very short', 'Très courts'),
    c('short', 'Courts'),
    c('chin-length', 'Au menton'),
    c('shoulder-length', 'Aux épaules'),
    c('long', 'Longs'),
    c('very long', 'Très longs'),
  ],
  hairStyle: [
    c('straight', 'Lisses'),
    c('loosely waved', 'Légèrement ondulés'),
    c('wavy', 'Ondulés'),
    c('curly', 'Bouclés'),
    c('coily', 'Crépus'),
    c('braided', 'Tressés'),
    c('in a bun', 'En chignon'),
    c('in a ponytail', 'En queue de cheval'),
    c('slicked back', 'Plaqués en arrière'),
  ],
  makeup: [
    c('no makeup', 'Sans maquillage'),
    c('minimal, natural-looking makeup', 'Naturel', 'Natural'),
    c('soft glam makeup', 'Glamour léger', 'Soft glam'),
    c('a bold lip', 'Lèvres marquées', 'Bold lip'),
    c('a smoky eye', 'Yeux charbonneux', 'Smoky eye'),
    c('editorial makeup', 'Éditorial', 'Editorial'),
  ],
};

/** Capitalised for display: a value read from a brief arrives lower-case. */
export const capitalise = (text: string): string =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

/**
 * The options for one field, labelled in `locale`. When the current value is
 * not one of them — something the brief or a photograph described in its own
 * words — it is offered too, as it is, so choosing nothing loses nothing.
 */
export function choicesFor(
  field: keyof ModelAppearance,
  value: string,
  locale: Locale,
): Array<{ value: string; label: string }> {
  const list = (APPEARANCE_CHOICES[field] ?? []).map((choice) => ({
    value: choice.value,
    label: locale === 'fr' ? choice.fr : choice.en,
  }));
  const current = value.trim();
  if (current && !list.some((option) => option.value === current)) {
    list.unshift({ value: current, label: capitalise(current) });
  }
  return list;
}
